import React, { useMemo, useState, useEffect, useRef } from 'react';
import './BookingTab.css';

import tourImg from '../assets/image/icon1.svg';
import hotelImg from '../assets/image/icon2.png';
import transportImg from '../assets/image/icon5.png';

import { DestinationSVG, TourTypeSVG, DurationSVG, HotelUserIcon, HotelMinusIcon, HotelPlusIcon } from '../components/SvgIcons';

function GuestCounterDropdown({ value = 1, onChange = null }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="dds" ref={dropdownRef}>
      <button type="button" className="dds-toggle" onClick={() => setIsOpen(!isOpen)}>
        <div className="buttonContent">
          <HotelUserIcon />
          <span className="dds-content">
            <span className="dds-label">Guest</span>
            <span className="dds-value">
              {value} {value > 1 ? 'People' : 'Person'}
            </span>
          </span>
        </div>
        <i className="bi bi-chevron-down"></i>
      </button>

      {isOpen && (
        <div className="guest-dropdown-menu">
          <div className="guest-item">
            <span className="guest-item-label">Person</span>
            <div className="guest-counter">
              <button
                type="button"
                className="counter-btn"
                onClick={() => onChange?.(Math.max(1, value - 1))}
              >
                <HotelMinusIcon />
              </button>
              <span className="counter-value">{value}</span>
              <button
                type="button"
                className="counter-btn"
                onClick={() => onChange?.(value + 1)}
              >
                <HotelPlusIcon />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DropdownSearch({ label, placeholder, options, value, onChange, icon: IconComponent }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((option) => option.title.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    function onDocClick(event) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target)) setOpen(false);
    }

    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  function selectOption(option) {
    onChange?.(option);
    setOpen(false);
  }

  return (
    <div className="dds" ref={containerRef}>
      <button type="button" className="dds-toggle" onClick={() => setOpen(!open)} aria-expanded={open}>
        <div className="buttonContent">
          <span className="dds-leading"><IconComponent /></span>
          <span className="dds-content">
            {label && <span className="dds-label">{label}</span>}
            <span className="dds-value">{value ? value.title : placeholder}</span>
          </span>
        </div>
        <i className="bi bi-chevron-down"></i>
      </button>

      {open && (
        <div className="dds-panel">
          {['Destination', 'Location', 'Location From'].includes(label) && (
            <div className="dds-search">
              <i className="bi bi-search"></i>
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Type to search"
              />
            </div>
          )}

          <div className="dds-list">
            {filtered.map((option) => (
              <button
                key={`${option.id ?? option.title}-${option.title}`}
                type="button"
                className="dds-item"
                onClick={() => selectOption(option)}
              >
                {option.title}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FormRow({ preset, bookingData = null, onSearch = null, initialFilters = null }) {
  const [destination, setDestination] = useState(initialFilters?.destination || null);
  const [type, setType] = useState(initialFilters?.type || null);
  const [duration, setDuration] = useState(initialFilters?.duration || null);
  const [guests, setGuests] = useState(initialFilters?.guests || 1);

  const fieldTextByPreset = {
    tour: {
      destinationLabel: 'Destination',
      destinationPlaceholder: 'Select Destination',
      typeLabel: 'Tour Type',
      typePlaceholder: 'Select Tour Type',
      durationLabel: 'Guest',
      durationPlaceholder: '1 Person',
      showGuest: true,
    },
    hotel: {
      destinationLabel: 'Location',
      destinationPlaceholder: 'Select Location',
      typeLabel: 'Room Type',
      typePlaceholder: 'Select Room Type',
      durationLabel: 'Guest',
      durationPlaceholder: '1 Person',
      showGuest: true,
    },
    transport: {
      destinationLabel: 'Location From',
      destinationPlaceholder: 'Select Location',
      typeLabel: 'Transport',
      typePlaceholder: 'Which Type',
      durationLabel: 'Trip Duration',
      durationPlaceholder: 'Select Duration',
      showGuest: false,
    },
  };

  const text = fieldTextByPreset[preset] || fieldTextByPreset.tour;

  const destinationOptions = useMemo(() => {
    if (preset === 'tour' && bookingData?.destinations?.length) {
      return bookingData.destinations;
    }

    return [{ id: 'ny', title: 'New York' }, { id: 'la', title: 'Los Angeles' }];
  }, [bookingData, preset]);

  const typeOptions = useMemo(() => {
    if (preset === 'tour' && bookingData?.tourTypes?.length) {
      return bookingData.tourTypes;
    }

    return [{ id: 'standard', title: 'Standard Room' }, { id: 'premium', title: 'Premium Room' }];
  }, [bookingData, preset]);

  const durationOptions = useMemo(() => {
    if (preset === 'tour' && bookingData?.durations?.length) {
      return bookingData.durations;
    }

    return [{ id: 'd3', title: '1 Night' }, { id: 'd4', title: '3 Nights' }];
  }, [bookingData, preset]);

  useEffect(() => {
    setDestination(initialFilters?.destination || null);
    setType(initialFilters?.type || null);
    setDuration(initialFilters?.duration || null);
    setGuests(initialFilters?.guests || 1);
  }, [initialFilters]);

  return (
    <form
      className="booking-form"
      onSubmit={(event) => {
        event.preventDefault();
        onSearch?.({
          preset,
          destination,
          type,
          duration,
          guests,
        });
      }}
    >
      <div className="booking-field">
        <DropdownSearch
          label={text.destinationLabel}
          placeholder={text.destinationPlaceholder}
          options={destinationOptions}
          value={destination}
          onChange={setDestination}
          icon={DestinationSVG}
        />
      </div>

      <div className="booking-field">
        <DropdownSearch
          label={text.typeLabel}
          placeholder={text.typePlaceholder}
          options={typeOptions}
          value={type}
          onChange={setType}
          icon={TourTypeSVG}
        />
      </div>

      <div className="booking-field">
        {text.showGuest ? (
          <GuestCounterDropdown value={guests} onChange={setGuests} />
        ) : (
          <DropdownSearch
            label={text.durationLabel}
            placeholder={text.durationPlaceholder}
            options={durationOptions}
            value={duration}
            onChange={setDuration}
            icon={DurationSVG}
          />
        )}
      </div>

      <div className="booking-actions">
        <button type="submit" className="primary-btn4 search">Search</button>
      </div>
    </form>
  );
}

function BookingTab({ bookingData = null, hasHero = true }) {
  const tabs = useMemo(
    () => [
      { key: 'tour', label: 'Tour', img: tourImg },
      { key: 'hotel', label: 'Hotel', img: hotelImg },
      { key: 'transport', label: 'Transport', img: transportImg },
    ],
    []
  );
  const [activeKey, setActiveKey] = useState('tour');
  const [filtersByTab, setFiltersByTab] = useState({
    tour: { destination: null, type: null, duration: null, guests: 1 },
    hotel: { destination: null, type: null, duration: null, guests: 1 },
    transport: { destination: null, type: null, duration: null, guests: 1 },
  });

  const handleSearch = (filters) => {
    setFiltersByTab((current) => ({
      ...current,
        [filters.preset]: {
          destination: filters.destination,
          type: filters.type,
          duration: filters.duration,
          guests: filters.guests,
        },
      }));
    bookingData?.onSearch?.(filters);
  };

  return (
    <div className={`booking-tabs-wrap ${hasHero ? '' : 'booking-tabs-wrap--no-hero'}`.trim()}>
      <div className="booking-tabs">
        {tabs.map((tab) => (
          <button
            type="button"
            key={tab.key}
            className={`booking-tab ${activeKey === tab.key ? 'active' : ''}`}
            onClick={() => setActiveKey(tab.key)}
            aria-pressed={activeKey === tab.key}
          >
            <img src={tab.img} alt={tab.label} className="tab-icon" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="booking-content">
        <FormRow
          preset={activeKey}
          bookingData={bookingData}
          onSearch={handleSearch}
          initialFilters={filtersByTab[activeKey]}
        />
      </div>
    </div>
  );
}

export default BookingTab;
